import {action, computed, makeObservable, observable, runInAction} from 'mobx'
import {makePersistable} from "mobx-persist-store"
import {mean, noop} from "lodash-es"
import {updatePosition} from "@ui/api/location"
import {sendDebug} from "@ui/api/track"
import store from "./"
import {
  calculateSpeed,
  getAverage,
  getAzimuth,
} from "@ui/common/utils"
import mapService from '@ui/services/MapService'
import movementService from '@ui/services/MovementService'
import positionService from '@ui/services/PositionService'
import routeService from '@ui/services/RouteService'
import neighbourService from "@ui/services/NeighbourService"

export type GeolocationPositionExt = {
	filter?: {
		accepted: boolean
		ratio: number
	}
} & GeolocationPosition

export interface IMovement {
	readonly current: GeolocationPosition | null
	readonly accepted: GeolocationPosition[]
	readonly movement: GeolocationPosition[]
	readonly isAvailable: boolean		// доступность навигатора
	readonly isFollowing: boolean		// следовать за текущими координатами
	readonly isVisible: boolean		// видимость другим участникам
	readonly isPositioned: boolean // установлена текущая позиция пользователя
  readonly isCompas: boolean		// носом по курсу
  readonly useAdapter: boolean // используем координаты из адаптера для движения
	
	init(): void
	switchFollowing(): boolean
	switchVisibility(): boolean
  switchCompas(): boolean
  switchAdapter(): void
}

export class Movement implements IMovement {
	current: GeolocationPosition | null = null
	accepted: GeolocationPosition[] = []
  movement: GeolocationPosition[] = []

  // для сохранения на бэке точек, для последующей тестирования
  #debug: GeolocationPositionExt[] = []

  #rotations: number[] = [0]

	isAvailable: boolean
	isFollowing: boolean
	isVisible: boolean
  isCompas: boolean
  useAdapter: boolean

	constructor() {
		this.isAvailable = movementService.isAvailable
		this.isFollowing = true
		this.isVisible = false
    this.isCompas = false
    this.useAdapter = false

		mapService.addDragMovedCallback(() => this.isFollowing && this.switchFollowing())

		makeObservable(this, {
			current: observable,
			accepted: observable,
			movement: observable,
			isFollowing: observable,
      isCompas: observable,
			isVisible: observable,
      useAdapter: observable,
			isPositioned: computed,

			init: action,
			switchFollowing: action,
			switchVisibility: action,
      switchCompas: action
		})

		makePersistable(this, {
			name: 'movement',
			properties: ["isFollowing", "isCompas"],
			storage: window.localStorage
		})
	}

  readonly movementMaxLength = 10 // максимальное кол-во точек в массиве, остальные можно удалять как избыточные
	readonly acceptedMaxLength = 30 // максимальное кол-во точек в массиве, остальные можно удалять как избыточные
	readonly maxSpeed = 30 // м/с
	readonly isAcceptedCount = 5 // кол-во точек достаточное для признания точки одобренной
  readonly isPositionedCount = 5 // кол-во одобренных точек достаточное для признания факта правильного местоположения
	readonly allowableDistanceRatio = 0.5 // минимальное отношение допустимых расстояний к недопустимым

	switchVisibility(): boolean {
		this.isVisible = !this.isVisible
		neighbourService.toggleLayer(this.isVisible)
		// !this.isVisible && neighbourService.hidePopup()
		return this.isVisible
	}

	#sendPosition = (position: GeolocationPosition) => {
    const {user} = store
    updatePosition(user.instance, position)
	    .then(neighbourService.updateNeighbours.bind(this))
	    .catch(error => {
	      const {debug} = store
	      debug.error(`updatePosition::error: ${error}`)
	    })
  }

	switchFollowing(): boolean {
		this.isFollowing = !this.isFollowing
		return this.isFollowing
	}

  switchCompas(): boolean {
		this.isCompas = !this.isCompas
		return this.isCompas
	}

  get isPositioned() {
    return this.accepted.length > 0 && this.movement.length < this.isPositionedCount
  }

	init(): void {
		movementService.start(
      this.#processNewPoint.bind(this),
      noop, // (position: GeolocationPositionError) => {}
    )
	}

  switchAdapter(): void {
    if (this.useAdapter) {
      this.#returnToDefaultData()
    } else {
      this.#switchToAdapterData()
    }
  }

  #switchToAdapterData() {
    const {adapter} = store
    movementService.stop()
    this.useAdapter = true
    adapter.updateCallbacks({
      onData: () => {
        const geo = adapter.getGeoPosition()
        if (geo) {
          this.#processNewPoint(geo)
        }
      },
      onClose: (wasClean) => {
        this.#returnToDefaultData()
      }
    })
  }

  #returnToDefaultData() {
    const {adapter} = store
    adapter.updateCallbacks({onData: noop, onClose: noop})
    this.useAdapter = false
    this.init()
  }

  #processNewPoint(position: GeolocationPosition) {
    if (this.#hasMoved(position)) {
      runInAction(() => this.current = position)
      if (this.isPositioned && this.#isAccepted(position)) {
        this.accepted = [position, ...this.accepted].toSpliced(this.acceptedMaxLength)
        this.movement = []
        this.#processAcceptedPoint(position)
      } else {
        this.movement = [position, ...this.movement].toSpliced(this.movementMaxLength)
        !this.isPositioned && this.#recalculateMovement()
      }
    }
  }

  #recalculateMovement(){
    const accepted = []
    for (let i= 0; i < this.movement.length; i++) {
      const A = this.movement[i]
      let acceptedDistance = 0
      for (let j= 0; j < this.movement.length - 1; j++) {
        const B = this.movement[j < i ? j : j+1]
        if (calculateSpeed(A, B) < this.maxSpeed) acceptedDistance++
      }
      const ratio = acceptedDistance / (this.movement.length - 1)
      if (ratio > this.allowableDistanceRatio) accepted.push(A)
    }
    if (accepted.length > this.isPositionedCount) {
      this.accepted = [...accepted, ...this.accepted].toSpliced(this.acceptedMaxLength)
      this.movement = []
    }
  }

  #isAccepted(point: GeolocationPosition): boolean {
    let acceptedDistances = 0, distances = 0
    for (let k= 0; k < this.accepted.length ; k++) {
      distances++
      if (calculateSpeed(point, this.accepted[k]) < this.maxSpeed) acceptedDistances++
      if (distances >= this.isAcceptedCount) break
    }
    const ratio= acceptedDistances / distances
    const accepted= ratio > this.allowableDistanceRatio

    // отключено, возможно еще понадобится, поэтому не выпиливаем
    // process.env.EMULATE_GEO_MOVING !== "true" && this.#sendTestData(point, ratio, accepted)

    return accepted
  }

  #sendTestData(point: GeolocationPosition, ratio: number, accepted: boolean) {
    this.#debug.push({...JSON.parse(JSON.stringify(point)), filter: {ratio, accepted}})
      // отправка на сервер данных по точкам
    if (this.#debug.length >= 50) {
    	const {user, debug} = store
    	sendDebug(user.instance, this.#debug)
        .then(() => this.#debug = [])
    		.catch(error => debug.error(`Track::sendDebug::error: ${error.code}, ${error.message}`))
    }
  }

  #hasMoved(position: GeolocationPosition): boolean {
		const {latitude, longitude} = position.coords
		const coords = this.current?.coords
		const moved = !(
			!!coords &&
			latitude == coords.latitude &&
			longitude == coords.longitude
		)
		return moved
	}

  #processAcceptedPoint(position: GeolocationPosition) {
    store.track.record(position)
    const point: [number, number] = [position.coords.longitude, position.coords.latitude]
    const rotation = this.#calculateRotation()
    positionService.setCurrent(point, -1 * rotation, position.coords.speed)
    if (this.isFollowing) {
      if (this.isCompas) mapService.moveToWithRotation(this.#addShift(point), {rotation, anchor: point}, 1000)
      else mapService.moveToWithRotation(point, {rotation: 0}, 1000)
    }
    if (this.isVisible) this.#sendPosition(position)
  }

  // в карту желательно передавать значение поворота от -Пи до Пи, поэтому такие вот приседания
  #normalizeRotation(rotation: number) {
    return rotation > Math.PI ? 2 * Math.PI - rotation : -1 * rotation
  }

  #calculateRotation() {
    const minPointsForRotation = 3      // минимальное кол-во принятых (accepted) точек, для расчета поворота карты
    const maxPointsForRotation = 7      // максимальное кол-во принятых (accepted) точек, для расчета поворота карты
    const rotationsMaxLength = 3        // длина массива поворотов (высчитываются на каждой новой точке)
                                        // по которому высчитываем среднее значение и поворачиваем карту

    if (this.accepted.length > minPointsForRotation) {
      // получаем две точки, через которые проходит средняя линия, полученная регрессией 1 порядка
      const points = getAverage(...this.accepted
        .slice(0, maxPointsForRotation)
        .map(({coords}) => [coords.longitude, coords.latitude])
        .reverse()
      )

      if (points) {
        // routeService.drawAverage(...points) // рисуем среднюю линию (это для тестирования)
        this.#rotations = [getAzimuth(...points), ...this.#rotations].toSpliced(rotationsMaxLength)
      }
    }

    const meanRotation = mean(this.#rotations) // арифм среднее для значений из массива поворотов

    return this.#normalizeRotation(meanRotation)
  }

  #addShift([x, y]: [number, number]): [number, number] {
    const [width, height] = mapService.getMapSize() || []
    const menuHeight = height - (store.settings.menuTop || height) + 40
    const coef = (height - menuHeight * 2) / height  // смещение от центра в низ экрана в процентах

    if (height && width) {
      const [topCenterX, topCenterY] = mapService.getCoordinateFromPixel([width / 2, height])
      const [bottomCenterX, bottomCenterY] = mapService.getCoordinateFromPixel([width / 2, 0])
      const deltaX = coef * (topCenterX - bottomCenterX) / 2
      const deltaY = coef * (topCenterY - bottomCenterY) / 2
      return [x - deltaX, y - deltaY]
    }
    return [x, y]
  }

}
